/* ============================================================
   PrintPeak Store - Theme JavaScript
   ============================================================ */

'use strict';

/* ---------- Utilities ---------- */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
const off = (el, ev, fn) => el && el.removeEventListener(ev, fn);
const fmt = (cents) => {
  const format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
  return Shopify.formatMoney ? Shopify.formatMoney(cents, format) : `$${(cents / 100).toFixed(2)}`;
};
const themeString = (group, key, fallback = '') => window.theme?.strings?.[group]?.[key] ?? fallback;
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- Focus Trap ---------- */
const FocusTrap = (() => {
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const traps = new Map();

  function getFocusable(container) {
    return $$(FOCUSABLE, container).filter(el => el.offsetParent !== null && !el.closest('[hidden]'));
  }

  function activate(container, options = {}) {
    if (!container) return;
    deactivate(container);
    const previouslyFocused = document.activeElement;
    const onKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = getFocusable(container);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', onKeyDown);
    traps.set(container, {
      onKeyDown,
      previouslyFocused: options.returnFocus === false ? null : previouslyFocused
    });
    const toFocus = options.initialFocus || getFocusable(container)[0];
    toFocus?.focus();
  }

  function deactivate(container) {
    if (!container) return;
    const trap = traps.get(container);
    if (!trap) return;
    container.removeEventListener('keydown', trap.onKeyDown);
    trap.previouslyFocused?.focus?.();
    traps.delete(container);
  }

  return { activate, deactivate, getFocusable };
})();

/* ---------- Toast ---------- */
const Toast = (() => {
  let container;
  function init() {
    container = document.createElement('div');
    container.id = 'toast-container';
    document.body.appendChild(container);
  }
  function show(msg, type = 'default', duration = 3500) {
    const el = document.createElement('div');
    const icon = type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ';
    el.className = `toast toast-${type}`;
    el.innerHTML = `<span>${icon}</span><span>${msg}</span>`;
    container.appendChild(el);
    setTimeout(() => {
      el.classList.add('hiding');
      el.addEventListener('animationend', () => el.remove());
    }, duration);
  }
  return { init, show };
})();

/* ---------- Header ---------- */
const Header = (() => {
  function init() {
    const header = $('.site-header');
    if (!header) return;
    let lastY = 0;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      header.classList.toggle('scrolled', y > 10);
      lastY = y;
    }, { passive: true });
  }
  return { init };
})();

/* ---------- Mobile Nav ---------- */
const MobileNav = (() => {
  function init() {
    const openBtn = $('#mobile-nav-open');
    const closeBtn = $('#mobile-nav-close');
    const nav = $('#mobile-nav');
    if (!nav) return;
    const toggle = (open) => {
      nav.classList.toggle('open', open);
      document.body.style.overflow = open ? 'hidden' : '';
      if (openBtn) openBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) FocusTrap.activate(nav, { initialFocus: closeBtn });
      else FocusTrap.deactivate(nav);
    };
    on(openBtn, 'click', () => toggle(true));
    on(closeBtn, 'click', () => toggle(false));
    on(document, 'keydown', (e) => {
      if (e.key === 'Escape' && nav.classList.contains('open')) toggle(false);
    });

    nav.querySelectorAll('.mobile-nav-expand').forEach(btn => {
      on(btn, 'click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (panel) panel.hidden = expanded;
      });
    });
  }
  return { init };
})();

/* ---------- Cart ---------- */
const Cart = (() => {
  const state = { items: [], itemCount: 0, totalPrice: 0 };

  async function fetch() {
    const res = await window.fetch('/cart.js');
    const data = await res.json();
    state.items = data.items;
    state.itemCount = data.item_count;
    state.totalPrice = data.total_price;
    return data;
  }

  async function add(variantId, qty = 1, properties = {}) {
    try {
      const res = await window.fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty, properties })
      });
      if (!res.ok) throw new Error('Could not add to cart');
      await fetch();
      updateUI();
      if (document.body.classList.contains('cart-drawer-enabled')) open();
      return true;
    } catch (e) {
      Toast.show(e.message || themeString('cart', 'addError', 'Could not add to cart'), 'error');
      return false;
    }
  }

  /** Add multiple variants in one request (mix & match packs). */
  async function addItems(items = []) {
    if (!items.length) return false;
    const merged = new Map();
    items.forEach((item) => {
      const id = Number(item.id);
      const qty = Math.max(1, Number(item.quantity) || 1);
      if (!id) return;
      const prev = merged.get(id) || { id, quantity: 0, properties: item.properties || {} };
      prev.quantity += qty;
      if (item.properties) prev.properties = { ...prev.properties, ...item.properties };
      merged.set(id, prev);
    });
    const payloadItems = [...merged.values()];
    if (!payloadItems.length) return false;

    try {
      const res = await window.fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: payloadItems })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.description || data.message || 'Could not add to cart');
      }
      await fetch();
      updateUI();
      if (document.body.classList.contains('cart-drawer-enabled')) open();
      return true;
    } catch (e) {
      Toast.show(e.message || themeString('cart', 'addError', 'Could not add to cart'), 'error');
      return false;
    }
  }

  async function buyNow(variantId, qty = 1, properties = {}) {
    try {
      const res = await window.fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty, properties })
      });
      if (!res.ok) throw new Error('Could not add to cart');
      window.location.href = '/checkout';
      return true;
    } catch (e) {
      Toast.show(e.message || 'Error processing checkout', 'error');
      return false;
    }
  }

  async function update(key, qty) {
    try {
      const res = await window.fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      });
      if (!res.ok) throw new Error('Cart update failed');
      const data = await fetch();
      updateUI();
      updateCartPage(data);
      if (qty === 0 && data.item_count > 0) Toast.show(themeString('cart', 'itemRemoved', 'Item removed'), 'default');
    } catch (e) {
      Toast.show(themeString('cart', 'updateError', 'Error updating cart'), 'error');
      throw e;
    }
  }

  async function remove(key) {
    await update(key, 0);
  }

  function open() {
    const drawer = $('#cart-drawer');
    const overlay = $('#cart-overlay');
    drawer && drawer.classList.add('open');
    overlay && overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
    FocusTrap.activate(drawer, { initialFocus: $('#cart-drawer-close') });
  }

  function close() {
    const drawer = $('#cart-drawer');
    const overlay = $('#cart-overlay');
    drawer && drawer.classList.remove('open');
    overlay && overlay.classList.remove('active');
    document.body.style.overflow = '';
    FocusTrap.deactivate(drawer);
  }

  function updateUI() {
    // Update count badges
    $$('[data-cart-count]').forEach(el => {
      el.textContent = state.itemCount;
      el.style.display = state.itemCount > 0 ? 'flex' : 'none';
    });

    const drawerCount = $('[data-cart-drawer-count]');
    if (drawerCount) {
      const label = state.itemCount === 1
        ? themeString('cart', 'itemOne', '{{ count }} item').replace('{{ count }}', state.itemCount)
        : themeString('cart', 'itemOther', '{{ count }} items').replace('{{ count }}', state.itemCount);
      drawerCount.textContent = `(${label})`;
    }

    renderItems();
    refreshUpsell();
    if (window.FreeShippingBar) window.FreeShippingBar.update(state.totalPrice);
  }

  function mountUpsellFromHost() {
    const slot = $('#cart-drawer-upsell');
    const host = document.querySelector('#cart-upsell-host .cart-upsell')
      || document.querySelector('#shopify-section-cart-upsell .cart-upsell');
    if (!slot || !host) return;
    slot.innerHTML = host.innerHTML;
  }

  async function refreshUpsell() {
    const slot = $('#cart-drawer-upsell');
    if (!slot) return;

    if (state.itemCount === 0) {
      slot.innerHTML = '';
      return;
    }

    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const res = await window.fetch(`${root}?sections=cart-upsell`);
      if (!res.ok) throw new Error('upsell fetch failed');
      const json = await res.json();
      const html = json['cart-upsell'];
      if (!html) {
        mountUpsellFromHost();
        return;
      }
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('.cart-upsell');
      slot.innerHTML = fresh ? fresh.innerHTML : '';
    } catch (_) {
      mountUpsellFromHost();
    }
  }

  function renderItems() {
    const container = $('#cart-drawer-items');
    const footer = $('#cart-drawer-footer');
    if (!container) return;

    if (state.items.length === 0) {
      container.innerHTML = `
        <div class="cart-empty">
          <div class="cart-empty-icon">🛒</div>
          <p>${themeString('cart', 'empty', 'Your cart is empty')}</p>
          <a href="/collections/all" class="btn btn-primary btn-sm">${themeString('cart', 'shopNow', 'Shop Now')}</a>
        </div>`;
      if (footer) footer.style.display = 'none';
      return;
    }

    if (footer) footer.style.display = 'block';
    container.innerHTML = state.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <img class="cart-item-image" src="${item.featured_image?.url || item.image}" alt="${item.title}">
        <div class="cart-item-info">
          <div class="cart-item-title">${item.product_title}</div>
          <div class="cart-item-variant">${item.variant_title !== 'Default Title' ? item.variant_title : ''}</div>
          <div class="cart-item-qty">
            <button class="qty-btn" data-action="decrease" data-key="${item.key}" data-qty="${item.quantity}">−</button>
            <span class="qty-input">${item.quantity}</span>
            <button class="qty-btn" data-action="increase" data-key="${item.key}" data-qty="${item.quantity}">+</button>
          </div>
          <button class="cart-item-remove" data-key="${item.key}">${themeString('cart', 'remove', 'Remove')}</button>
        </div>
        <div class="cart-item-price">${fmt(item.final_line_price)}</div>
      </div>`).join('');

    // Price totals
    const subtotalEl = $('#cart-subtotal-price');
    const totalEl = $('#cart-total-price');
    if (subtotalEl) subtotalEl.textContent = fmt(state.totalPrice);
    if (totalEl) totalEl.textContent = fmt(state.totalPrice);

    // Bind qty/remove buttons
    container.querySelectorAll('[data-action]').forEach(btn => {
      on(btn, 'click', () => {
        const key = btn.dataset.key;
        const qty = parseInt(btn.dataset.qty);
        const newQty = btn.dataset.action === 'increase' ? qty + 1 : Math.max(0, qty - 1);
        update(key, newQty);
      });
    });
    container.querySelectorAll('.cart-item-remove').forEach(btn => {
      on(btn, 'click', () => remove(btn.dataset.key));
    });
  }

  function updateCartPage(data) {
    const section = $('.main-cart-section');
    if (!section) return;

    if (data.item_count === 0) {
      window.location.reload();
      return;
    }

    const titleSpan = section.querySelector('.cart-page-title span');
    if (titleSpan) {
      const label = data.item_count === 1
        ? themeString('cart', 'itemOne', '{{ count }} item').replace('{{ count }}', data.item_count)
        : themeString('cart', 'itemOther', '{{ count }} items').replace('{{ count }}', data.item_count);
      titleSpan.textContent = `(${label})`;
    }

    const cartKeys = new Set(data.items.map(item => item.key));
    section.querySelectorAll('.cart-page-item').forEach(row => {
      if (!cartKeys.has(row.dataset.key)) row.remove();
    });

    data.items.forEach(item => {
      const row = section.querySelector(`.cart-page-item[data-key="${CSS.escape(item.key)}"]`);
      if (!row) return;

      const qtyInput = row.querySelector('.cart-qty-input');
      if (qtyInput) qtyInput.value = item.quantity;

      row.querySelectorAll('.qty-btn[data-key]').forEach(btn => {
        btn.dataset.qty = item.quantity;
        btn.disabled = false;
        btn.textContent = btn.dataset.action === 'decrease' ? '−' : '+';
      });

      const lineTotal = row.querySelector('.cart-page-item-total');
      if (lineTotal) lineTotal.textContent = fmt(item.final_line_price);

      const priceCell = row.querySelector('.cart-page-item-price');
      if (priceCell) {
        if (item.original_price !== item.final_price) {
          priceCell.innerHTML = `<span class="price-sale">${fmt(item.final_price)}</span><span class="price-compare">${fmt(item.original_price)}</span>`;
        } else {
          priceCell.textContent = fmt(item.original_price);
        }
      }
    });

    section.querySelectorAll('[data-cart-page-subtotal]').forEach(el => {
      el.textContent = fmt(data.total_price);
    });

    if (window.FreeShippingBar) window.FreeShippingBar.update(data.total_price);
  }

  function initCartPage() {
    const section = $('.main-cart-section');
    if (!section) return;

    on(section, 'click', async (e) => {
      const qtyBtn = e.target.closest('.qty-btn[data-key]');
      if (qtyBtn && !qtyBtn.disabled) {
        e.preventDefault();
        const key = qtyBtn.dataset.key;
        const qty = parseInt(qtyBtn.dataset.qty, 10);
        const newQty = qtyBtn.dataset.action === 'increase' ? qty + 1 : Math.max(0, qty - 1);
        qtyBtn.disabled = true;
        try {
          await update(key, newQty);
        } catch {
          qtyBtn.disabled = false;
        }
        return;
      }

      const removeBtn = e.target.closest('.cart-page-remove');
      if (removeBtn && !removeBtn.disabled) {
        e.preventDefault();
        removeBtn.disabled = true;
        removeBtn.textContent = themeString('cart', 'removing', 'Removing…');
        try {
          await update(removeBtn.dataset.key, 0);
        } catch {
          removeBtn.disabled = false;
          removeBtn.textContent = themeString('cart', 'remove', 'Remove');
        }
      }
    });
  }

  function init() {
    const cartBtn = $('#cart-icon-btn');
    if (cartBtn) {
      on(cartBtn, 'click', () => {
        if (document.body.classList.contains('cart-drawer-enabled')) {
          open();
        } else {
          window.location.href = '/cart';
        }
      });
    }
    on($('#cart-drawer-close'), 'click', close);
    on($('#cart-overlay'), 'click', close);
    on($('#cart-continue-btn'), 'click', close);
    on(document, 'keydown', (e) => {
      if (e.key === 'Escape') {
        const drawer = $('#cart-drawer');
        if (drawer?.classList.contains('open')) close();
      }
    });
    if (document.body.classList.contains('cart-drawer-enabled')) {
      fetch().then(updateUI);
      mountUpsellFromHost();
    }
    initCartPage();

    on(document, 'click', async (e) => {
      const btn = e.target.closest('[data-upsell-add]');
      if (!btn || btn.disabled) return;
      const id = btn.dataset.variantId;
      if (!id) return;
      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = themeString('cart', 'adding', 'Adding...');
      const ok = await add(Number(id), 1);
      btn.disabled = false;
      btn.textContent = ok ? 'Added' : original;
      if (ok) setTimeout(() => { if (btn.isConnected) btn.textContent = 'Add'; }, 1400);
    });
  }

  return { init, add, addItems, buyNow, open, close, fetch, updateUI };
})();

/* ---------- Volume Buy (Buy X mix variants) ---------- */
const VolumeBuy = (() => {
  function parseJson(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent.trim());
    } catch (_) {
      return null;
    }
  }

  function findVariant(variants, options) {
    return variants.find((v) => {
      for (let i = 0; i < options.length; i++) {
        if (String(v[`option${i + 1}`] || '') !== String(options[i])) return false;
      }
      return true;
    });
  }

  function selectedQty(root) {
    const radio = root.querySelector('.volume-buy__radio:checked');
    return radio ? parseInt(radio.dataset.qty, 10) || 0 : 0;
  }

  function markSelectedTier(root) {
    root.querySelectorAll('.volume-buy__tier').forEach((tier) => {
      const input = tier.querySelector('.volume-buy__radio');
      tier.classList.toggle('is-selected', !!(input && input.checked));
    });
  }

  function buildRows(root, qty) {
    const rowsEl = root.querySelector('[data-volume-rows]');
    const options = parseJson(root.querySelector('[data-volume-options]')) || [];
    if (!rowsEl) return;

    const onlyDefault =
      options.length === 1 &&
      options[0].values &&
      options[0].values.length === 1 &&
      options[0].values[0] === 'Default Title';

    rowsEl.innerHTML = '';
    for (let i = 1; i <= qty; i++) {
      const row = document.createElement('div');
      row.className = 'volume-buy__row';
      row.dataset.itemIndex = String(i);

      const label = document.createElement('div');
      label.className = 'volume-buy__row-label';
      label.textContent = `#${i}`;
      row.appendChild(label);

      const opts = document.createElement('div');
      opts.className = 'volume-buy__row-opts';

      if (onlyDefault || !options.length) {
        opts.innerHTML = '<span class="volume-buy__row-hint">One style — ready to add</span>';
      } else {
        options.forEach((opt, optIdx) => {
          const field = document.createElement('div');
          field.className = 'volume-buy__field';
          const id = `vb-${root.id}-i${i}-o${optIdx}`;
          field.innerHTML = `<label for="${id}">${opt.name}</label>`;
          const select = document.createElement('select');
          select.id = id;
          select.dataset.optionIndex = String(optIdx);
          (opt.values || []).forEach((val) => {
            const option = document.createElement('option');
            option.value = val;
            option.textContent = val;
            select.appendChild(option);
          });
          field.appendChild(select);
          opts.appendChild(field);
        });
      }

      row.appendChild(opts);
      rowsEl.appendChild(row);
    }
  }

  function updateSummary(root, qty) {
    const meta = root.querySelector('[data-volume-summary-meta]');
    const addBtn = root.querySelector('[data-volume-add]');
    const optionNames = (parseJson(root.querySelector('[data-volume-options]')) || [])
      .map((o) => o.name)
      .filter((n) => n && n !== 'Title');
    const mixLabel = optionNames.length
      ? `Mix ${optionNames.join(' & ').toLowerCase()} · ${qty} item${qty === 1 ? '' : 's'}`
      : `${qty} item${qty === 1 ? '' : 's'} in this pack`;

    if (meta) meta.textContent = mixLabel;
    if (addBtn) addBtn.textContent = `Add ${qty}-pack to cart`;
  }

  function collectItems(root) {
    const variants = parseJson(root.querySelector('[data-volume-variants]')) || [];
    const optionsMeta = parseJson(root.querySelector('[data-volume-options]')) || [];
    const rows = [...root.querySelectorAll('.volume-buy__row')];
    const items = [];
    const errors = [];

    rows.forEach((row, idx) => {
      const selects = [...row.querySelectorAll('select')];
      let variant;

      if (!selects.length) {
        variant = variants.find((v) => v.available) || variants[0];
      } else {
        const chosen = selects.map((s) => s.value);
        // Pad to match product option count if needed
        while (chosen.length < optionsMeta.length) chosen.push(optionsMeta[chosen.length]?.values?.[0]);
        variant = findVariant(variants, chosen);
      }

      if (!variant) {
        errors.push(`Item ${idx + 1}: combination not found`);
        return;
      }
      if (!variant.available) {
        errors.push(`Item ${idx + 1}: sold out`);
        return;
      }
      items.push({ id: variant.id, quantity: 1 });
    });

    return { items, errors };
  }

  function setError(root, msg) {
    const el = root.querySelector('[data-volume-error]');
    if (!el) return;
    if (!msg) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.textContent = msg;
  }

  function bindRoot(root) {
    const builder = root.querySelector('[data-volume-builder]');

    const rebuild = () => {
      markSelectedTier(root);
      const qty = selectedQty(root);
      if (qty > 0) {
        buildRows(root, qty);
        updateSummary(root, qty);
        if (builder) builder.open = true;
      }
      setError(root, '');
    };

    root.querySelectorAll('.volume-buy__radio').forEach((radio) => {
      on(radio, 'change', rebuild);
    });

    const addBtn = root.querySelector('[data-volume-add]');
    if (addBtn) {
      on(addBtn, 'click', async () => {
        const { items, errors } = collectItems(root);
        if (errors.length) {
          setError(root, errors[0]);
          return;
        }
        if (!items.length) {
          setError(root, 'Select options for each item');
          return;
        }
        addBtn.disabled = true;
        const ok = await Cart.addItems(items);
        addBtn.disabled = false;
        if (ok) {
          setError(root, '');
          Toast.show(themeString('cart', 'added', 'Added to cart'), 'success');
        }
      });
    }

    rebuild();
  }

  function init() {
    $$('[data-volume-buy]').forEach(bindRoot);
  }

  return { init };
})();

/* ---------- Product Form ---------- */
const ProductForm = (() => {
  function init() {
    $$('[data-product-form]').forEach(form => {
      on(form, 'submit', async (e) => {
        e.preventDefault();
        const submitBtn = form.querySelector('[type="submit"]');
        const variantInput = form.querySelector('[name="id"]');
        if (!variantInput) return;

        submitBtn.disabled = true;
        submitBtn.innerHTML = `<span class="spinner"></span> ${themeString('cart', 'adding', 'Adding...')}`;

        const formData = new FormData(form);
        const variantId = formData.get('id');
        const qty = parseInt(formData.get('quantity') || 1);

        const ok = await Cart.add(variantId, qty);
        if (ok) Toast.show(themeString('cart', 'added', 'Added to cart!'), 'success');

        submitBtn.disabled = false;
        submitBtn.innerHTML = submitBtn.dataset.defaultText || 'Add to cart';
      });
    });

    $$('[data-buy-now]').forEach(btn => {
      on(btn, 'click', async (e) => {
        e.preventDefault();
        const form = btn.closest('form');
        if (!form) return;

        const variantInput = form.querySelector('[name="id"]');
        if (!variantInput) return;

        const originalHTML = btn.innerHTML;
        btn.disabled = true;
        btn.innerHTML = '<span class="spinner"></span> Processing...';

        const formData = new FormData(form);
        const variantId = formData.get('id');
        const qty = parseInt(formData.get('quantity') || 1);

        await Cart.buyNow(variantId, qty);

        btn.disabled = false;
        btn.innerHTML = originalHTML;
      });
    });
  }
  return { init };
})();

/* ---------- Variant Selector ---------- */
const VariantSelector = (() => {
  function updatePriceDisplay(match, scope) {
    const priceWrapper = $('[data-product-price-wrapper]', scope || document);
    $$('[data-product-price]', scope || document).forEach(priceEl => {
      if (!match) return;
      if (match.compare_at_price && match.compare_at_price > match.price) {
        priceEl.textContent = fmt(match.price);
        priceEl.className = priceEl.closest('[data-product-price-wrapper]') ? 'price-sale' : priceEl.className;
      } else {
        priceEl.textContent = fmt(match.price);
        if (priceEl.closest('[data-product-price-wrapper]')) {
          priceEl.className = 'price-regular';
        }
      }
    });

    const compareEl = priceWrapper?.querySelector('.price-compare');
    if (compareEl) {
      if (match.compare_at_price && match.compare_at_price > match.price) {
        compareEl.textContent = fmt(match.compare_at_price);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }
  }

  /** Prefer exact variant image; else any variant sharing the same color (option that has an image). */
  function resolveVariantImage(variants, match, selected) {
    if (match?.featured_image?.src) return match.featured_image;

    // Same full option combo with an image (rare duplicate)
    const exact = variants.find(v =>
      v.featured_image?.src &&
      v.options.every((opt, i) => selected[`option${i + 1}`] === opt)
    );
    if (exact) return exact.featured_image;

    // Match by each selected option individually (color usually drives imagery)
    for (let i = 0; i < 3; i++) {
      const key = `option${i + 1}`;
      const val = selected[key];
      if (!val) continue;
      const byOption = variants.find(v => v.featured_image?.src && v.options[i] === val);
      if (byOption) return byOption.featured_image;
    }
    return null;
  }

  function galleryImageUrl(src) {
    if (!src) return '';
    // Prefer ~900px wide like liquid image_url; keep existing query if present
    if (/([?&])width=\d+/.test(src)) return src.replace(/([?&])width=\d+/, '$1width=900');
    return src.includes('?') ? `${src}&width=900` : `${src}?width=900`;
  }

  function updateGallery(featuredImage, scope) {
    if (!featuredImage?.src) return;
    const root = scope || document;
    const mainImg = root.querySelector('[data-gallery-main-img]') || root.querySelector('#gallery-main-img') || $('#gallery-main-img');
    if (!mainImg) return;

    const nextSrc = galleryImageUrl(featuredImage.src);
    if (mainImg.src === nextSrc || mainImg.src.endsWith(nextSrc.replace(/^https?:/, ''))) {
      // still sync thumbs
    } else {
      const swap = () => {
        mainImg.src = nextSrc;
        if (featuredImage.alt) mainImg.alt = featuredImage.alt;
        mainImg.style.opacity = '1';
      };
      if (prefersReducedMotion()) {
        swap();
      } else {
        mainImg.style.opacity = '0';
        const onLoad = () => {
          mainImg.style.opacity = '1';
          mainImg.removeEventListener('load', onLoad);
        };
        mainImg.addEventListener('load', onLoad);
        mainImg.src = nextSrc;
        if (featuredImage.alt) mainImg.alt = featuredImage.alt;
      }
    }

    const imageId = featuredImage.id != null ? String(featuredImage.id) : '';
    $$('[data-gallery-thumb]', root).forEach(thumb => {
      const match = imageId && String(thumb.dataset.imageId) === imageId;
      thumb.classList.toggle('active', !!match);
    });
  }

  function syncUrl(variantId) {
    if (!variantId || !window.history?.replaceState) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    } catch (_) { /* ignore */ }
  }

  function init() {
    $$('[data-variant-selector]').forEach(wrapper => {
      const optionBtns = $$('[data-option-btn]', wrapper);
      const form = wrapper.closest('form');
      const scope = wrapper.closest('.product-page') || form || document;
      const variantInput = $('[name="id"]', form || document);
      const addBtn = $('[data-add-to-cart]', form || document);

      let variants = [];
      try {
        variants = JSON.parse(wrapper.dataset.variants || '[]');
      } catch (_) {
        variants = [];
      }

      const selected = {};
      optionBtns.forEach(btn => {
        const { option, value } = btn.dataset;
        if (btn.classList.contains('active')) selected[option] = value;
      });

      optionBtns.forEach(btn => {
        on(btn, 'click', () => {
          const { option, value } = btn.dataset;
          selected[option] = value;

          $$(`[data-option="${option}"]`, wrapper).forEach(b => b.classList.remove('active'));
          btn.classList.add('active');

          const optionNum = option.replace('option', '');
          const labelEl = wrapper.querySelector(`[data-selected-${optionNum}]`);
          if (labelEl) labelEl.textContent = value;

          const match = variants.find(v =>
            v.options && v.options.every((opt, i) => selected[`option${i + 1}`] === opt)
          );
          if (!match) return;

          if (variantInput) variantInput.value = match.id;
          updatePriceDisplay(match, scope);
          if (addBtn) {
            addBtn.disabled = !match.available;
            addBtn.textContent = match.available
              ? `Add to Cart — ${fmt(match.price)}`
              : themeString('product', 'soldOut', 'Sold Out');
          }

          const image = resolveVariantImage(variants, match, selected);
          updateGallery(image, scope);
          syncUrl(match.id);
        });
      });
    });
  }
  return { init };
})();

/* ---------- Quantity Input ---------- */
const QtyInput = (() => {
  function init() {
    $$('.quantity-wrapper').forEach(wrapper => {
      const field = wrapper.querySelector('.quantity-field');
      wrapper.querySelector('[data-action="decrease"]')?.addEventListener('click', () => {
        const val = parseInt(field.value) - 1;
        field.value = Math.max(1, val);
      });
      wrapper.querySelector('[data-action="increase"]')?.addEventListener('click', () => {
        field.value = parseInt(field.value) + 1;
      });
    });
  }
  return { init };
})();

/* ---------- Announcement Bar ---------- */
const AnnouncementBar = (() => {
  function init() {
    const bar = $('.announcement-bar');
    const closeBtn = $('.announcement-bar .close-btn');
    if (!bar || !closeBtn) return;
    const key = 'announcement-hidden';
    if (sessionStorage.getItem(key)) { bar.style.display = 'none'; return; }
    on(closeBtn, 'click', () => {
      if (prefersReducedMotion()) {
        bar.style.display = 'none';
      } else {
        bar.style.height = bar.offsetHeight + 'px';
        bar.style.transition = 'all 0.3s ease';
        requestAnimationFrame(() => { bar.style.height = '0'; bar.style.opacity = '0'; bar.style.padding = '0'; });
      }
      sessionStorage.setItem(key, '1');
    });
  }
  return { init };
})();

/* ---------- Scroll Animations ---------- */
const ScrollAnim = (() => {
  function init() {
    const els = $$('[data-animate]');
    if (!els.length) return;
    if (prefersReducedMotion()) {
      els.forEach(el => el.classList.add('animated'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          const delay = e.target.dataset.delay || 0;
          setTimeout(() => e.target.classList.add('animated'), delay);
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    els.forEach(el => io.observe(el));
  }
  return { init };
})();

/* ---------- Image Gallery + Lightbox Zoom (Product Page) ---------- */
const Gallery = (() => {
  let lightbox = null;
  let scale = 1;
  let pos = { x: 0, y: 0 };
  let dragging = false;
  let dragStart = { x: 0, y: 0 };
  let images = [];
  let index = 0;

  function collectImages(main) {
    const thumbs = $$('[data-gallery-thumb]');
    if (thumbs.length) {
      return thumbs.map((thumb, i) => ({
        src: thumb.dataset.full || thumb.querySelector('img')?.src,
        alt: thumb.querySelector('img')?.alt || `Image ${i + 1}`,
        id: thumb.dataset.imageId || String(i)
      })).filter(img => img.src);
    }
    const mainImg = main.querySelector('[data-gallery-main-img]');
    if (!mainImg?.src) return [];
    return [{ src: mainImg.src, alt: mainImg.alt || '', id: '0' }];
  }

  function ensureLightbox() {
    if (lightbox) return lightbox;
    lightbox = document.createElement('div');
    lightbox.id = 'product-media-lightbox';
    lightbox.className = 'media-lightbox';
    lightbox.hidden = true;
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.setAttribute('aria-label', 'Product image viewer');
    lightbox.innerHTML = `
      <div class="media-lightbox__backdrop" data-lightbox-close></div>
      <div class="media-lightbox__panel">
        <button type="button" class="media-lightbox__close" data-lightbox-close aria-label="Close">&times;</button>
        <button type="button" class="media-lightbox__nav media-lightbox__nav--prev" data-lightbox-prev aria-label="Previous image">‹</button>
        <button type="button" class="media-lightbox__nav media-lightbox__nav--next" data-lightbox-next aria-label="Next image">›</button>
        <div class="media-lightbox__stage">
          <img class="media-lightbox__img" alt="" draggable="false">
        </div>
        <div class="media-lightbox__toolbar">
          <button type="button" data-lightbox-zoom-out aria-label="Zoom out">−</button>
          <span class="media-lightbox__zoom-label" data-lightbox-zoom-label>100%</span>
          <button type="button" data-lightbox-zoom-in aria-label="Zoom in">+</button>
          <button type="button" data-lightbox-reset aria-label="Reset zoom">Reset</button>
        </div>
        <div class="media-lightbox__thumbs" data-lightbox-thumbs></div>
      </div>
    `;
    document.body.appendChild(lightbox);

    on(lightbox, 'click', (e) => {
      if (e.target.closest('[data-lightbox-close]')) close();
      if (e.target.closest('[data-lightbox-prev]')) show(index - 1);
      if (e.target.closest('[data-lightbox-next]')) show(index + 1);
      if (e.target.closest('[data-lightbox-zoom-in]')) setZoom(scale + 0.25);
      if (e.target.closest('[data-lightbox-zoom-out]')) setZoom(scale - 0.25);
      if (e.target.closest('[data-lightbox-reset]')) resetZoom();
    });

    const img = lightbox.querySelector('.media-lightbox__img');
    on(img, 'wheel', (e) => {
      e.preventDefault();
      setZoom(scale + (e.deltaY < 0 ? 0.15 : -0.15));
    }, { passive: false });

    on(img, 'pointerdown', (e) => {
      if (scale <= 1) return;
      dragging = true;
      dragStart = { x: e.clientX - pos.x, y: e.clientY - pos.y };
      img.setPointerCapture(e.pointerId);
    });
    on(img, 'pointermove', (e) => {
      if (!dragging) return;
      pos = { x: e.clientX - dragStart.x, y: e.clientY - dragStart.y };
      applyTransform();
    });
    on(img, 'pointerup', () => { dragging = false; });
    on(img, 'pointercancel', () => { dragging = false; });

    on(document, 'keydown', (e) => {
      if (lightbox.hidden) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') show(index - 1);
      if (e.key === 'ArrowRight') show(index + 1);
      if (e.key === '+' || e.key === '=') setZoom(scale + 0.25);
      if (e.key === '-') setZoom(scale - 0.25);
    });

    return lightbox;
  }

  function applyTransform() {
    const img = lightbox.querySelector('.media-lightbox__img');
    if (!img) return;
    img.style.transform = `translate(${pos.x}px, ${pos.y}px) scale(${scale})`;
    img.classList.toggle('is-zoomed', scale > 1);
    const label = lightbox.querySelector('[data-lightbox-zoom-label]');
    if (label) label.textContent = `${Math.round(scale * 100)}%`;
  }

  function setZoom(next) {
    scale = Math.min(4, Math.max(1, next));
    if (scale === 1) pos = { x: 0, y: 0 };
    applyTransform();
  }

  function resetZoom() {
    scale = 1;
    pos = { x: 0, y: 0 };
    applyTransform();
  }

  function renderThumbs() {
    const wrap = lightbox.querySelector('[data-lightbox-thumbs]');
    if (!wrap) return;
    wrap.innerHTML = images.map((img, i) => `
      <button type="button" class="media-lightbox__thumb${i === index ? ' is-active' : ''}" data-lightbox-thumb="${i}" aria-label="View image ${i + 1}">
        <img src="${img.src}" alt="" loading="lazy">
      </button>
    `).join('');
    wrap.querySelectorAll('[data-lightbox-thumb]').forEach(btn => {
      on(btn, 'click', () => show(Number(btn.dataset.lightboxThumb)));
    });
  }

  function show(i) {
    if (!images.length) return;
    index = (i + images.length) % images.length;
    const current = images[index];
    const img = lightbox.querySelector('.media-lightbox__img');
    resetZoom();
    img.src = current.src;
    img.alt = current.alt || '';
    renderThumbs();
    lightbox.querySelector('[data-lightbox-prev]').hidden = images.length < 2;
    lightbox.querySelector('[data-lightbox-next]').hidden = images.length < 2;
  }

  function open(startIndex = 0) {
    ensureLightbox();
    show(startIndex);
    lightbox.hidden = false;
    document.body.style.overflow = 'hidden';
    FocusTrap.activate(lightbox, { initialFocus: lightbox.querySelector('[data-lightbox-close]') });
  }

  function close() {
    if (!lightbox || lightbox.hidden) return;
    lightbox.hidden = true;
    document.body.style.overflow = '';
    FocusTrap.deactivate(lightbox);
    resetZoom();
  }

  function syncMainFromThumb(main, thumb, thumbs) {
    thumbs.forEach(t => t.classList.remove('active'));
    thumb.classList.add('active');
    const img = main.querySelector('img');
    if (!img) return;
    const next = thumb.dataset.full || thumb.querySelector('img')?.src;
    if (!next) return;
    const swap = () => {
      img.src = next;
      img.style.opacity = '1';
    };
    if (prefersReducedMotion()) {
      swap();
    } else {
      img.style.opacity = '0';
      img.onload = swap;
      img.src = next;
    }
  }

  function init() {
    const main = $('#gallery-main');
    if (!main) return;
    const thumbs = $$('[data-gallery-thumb]');
    images = collectImages(main);

    thumbs.forEach((thumb, i) => {
      on(thumb, 'click', () => syncMainFromThumb(main, thumb, thumbs));
      on(thumb, 'dblclick', (e) => {
        e.preventDefault();
        open(i);
      });
    });

    const mainImg = main.querySelector('[data-gallery-main-img]');
    if (mainImg) {
      main.classList.add('gallery-main-wrap--zoomable');
      mainImg.style.cursor = 'zoom-in';
      on(main, 'click', (e) => {
        if (e.target.closest('[data-gallery-thumb]')) return;
        const activeIdx = Math.max(0, thumbs.findIndex(t => t.classList.contains('active')));
        open(thumbs.length ? activeIdx : 0);
      });
      if (!main.querySelector('.gallery-zoom-btn')) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'gallery-zoom-btn';
        btn.setAttribute('aria-label', 'Open image viewer');
        btn.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/><path d="M11 8v6M8 11h6"/></svg>`;
        on(btn, 'click', (e) => {
          e.stopPropagation();
          const activeIdx = Math.max(0, thumbs.findIndex(t => t.classList.contains('active')));
          open(thumbs.length ? activeIdx : 0);
        });
        main.appendChild(btn);
      }
    }
  }

  return { init, open, close };
})();

/* ---------- Sticky ATC Bar (Mobile) ---------- */
const StickyATC = (() => {
  function init() {
    const bar = $('#sticky-atc');
    if (!bar) return;
    const productSection = $('.product-info');
    if (!productSection) return;
    const io = new IntersectionObserver(([e]) => {
      bar.classList.toggle('visible', !e.isIntersecting);
    }, { threshold: 0 });
    io.observe(productSection);
  }
  return { init };
})();

/* ---------- Lazy Image (Wokiee technique) ---------- */
const LazyImage = (() => {
  function loadImg(img) {
    const src = img.dataset.src;
    if (!src) return;
    const hi = new Image();
    hi.onload = () => {
      img.src = src;
      img.classList.add('loaded');
      delete img.dataset.src;
    };
    hi.src = src;
  }

  function init() {
    const imgs = $$('img[data-src]');
    if (!imgs.length) return;

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            loadImg(e.target);
            io.unobserve(e.target);
          }
        });
      }, { rootMargin: '200px 0px' }); // Pre-load 200px before entering viewport
      imgs.forEach(img => io.observe(img));
    } else {
      // Fallback: load all immediately
      imgs.forEach(loadImg);
    }
  }

  return { init };
})();

/* ---------- Quick Add (direct fetch, no window.Cart dependency) ---------- */
const QuickAdd = (() => {
  function init() {
    on(document, 'click', async (e) => {
      const btn = e.target.closest('.quick-add-btn');
      if (!btn) return;

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      const originalHTML = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<span class="spinner"></span>';

      try {
        const res = await window.fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });
        if (!res.ok) throw new Error('Add to cart failed');

        // Refresh cart state
        const cartRes = await window.fetch('/cart.js');
        const cartData = await cartRes.json();

        // Update count badges
        $$('[data-cart-count]').forEach(el => {
          el.textContent = cartData.item_count;
          el.style.display = cartData.item_count > 0 ? 'flex' : 'none';
        });

        if (window.FreeShippingBar) window.FreeShippingBar.update(cartData.total_price);

        // Open cart drawer if available
        if (typeof Cart !== 'undefined') {
          Cart.fetch().then(() => {
            Cart.updateUI();
            if (document.body.classList.contains('cart-drawer-enabled')) {
              Cart.open();
            } else {
              window.location.href = '/cart';
            }
          });
        }

        Toast.show(themeString('cart', 'added', 'Added to cart!'), 'success');
      } catch (err) {
        console.error('[QuickAdd]', err);
        Toast.show(themeString('cart', 'addError', 'Could not add to cart'), 'error');
      } finally {
        btn.disabled = false;
        btn.innerHTML = originalHTML;
      }
    });
  }

  return { init };
})();

/* ---------- Quick Add Modal (Choose Options) ---------- */
const QuickAddModal = (() => {
  let modal, backdrop, closeBtn, loadingEl, contentEl;
  let currentProduct = null;
  let selectedOptions = {};
  let selectedVariant = null;
  let isOpen = false;

  function isColorOption(name) {
    const n = String(name || '').toLowerCase();
    return n === 'color' || n === 'colour';
  }

  function getOptionName(option, index) {
    if (typeof option === 'string') return option;
    if (option && typeof option === 'object' && option.name) return option.name;
    return `Option ${index + 1}`;
  }

  function getOptionValues(product, optionKey, optIndex) {
    const opt = product.options[optIndex];
    if (opt && typeof opt === 'object' && Array.isArray(opt.values)) {
      return opt.values;
    }
    return [...new Set(product.variants.map(v => v[optionKey]).filter(Boolean))];
  }

  function getOptionCount(product) {
    if (Array.isArray(product.options) && product.options.length) {
      return product.options.length;
    }
    const firstVariant = product.variants[0];
    if (!firstVariant) return 0;
    return [1, 2, 3].filter(i => firstVariant[`option${i}`]).length;
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function show() {
    if (!modal) return;
    modal.hidden = false;
    isOpen = true;
    document.body.style.overflow = 'hidden';
    FocusTrap.activate(modal, { initialFocus: closeBtn });
  }

  function hide() {
    if (!modal) return;
    modal.hidden = true;
    isOpen = false;
    document.body.style.overflow = '';
    FocusTrap.deactivate(modal);
    currentProduct = null;
    selectedVariant = null;
    selectedOptions = {};
    if (loadingEl) loadingEl.hidden = false;
    if (contentEl) contentEl.hidden = true;
  }

  function findVariant(product, options) {
    const count = getOptionCount(product);
    return product.variants.find(v =>
      Array.from({ length: count }, (_, i) => i + 1).every(i => v[`option${i}`] === options[`option${i}`])
    );
  }

  function updateVariantUI() {
    if (!currentProduct) return;

    selectedVariant = findVariant(currentProduct, selectedOptions);
    if (!selectedVariant) return;

    const priceEl = $('#qam-price');
    const addBtn = $('#qam-add-btn');
    const imgEl = $('#qam-image');

    if (priceEl) {
      if (selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price) {
        priceEl.innerHTML = `<span class="price-sale">${fmt(selectedVariant.price)}</span> <span class="price-compare">${fmt(selectedVariant.compare_at_price)}</span>`;
      } else {
        priceEl.innerHTML = `<span class="price-regular">${fmt(selectedVariant.price)}</span>`;
      }
    }

    if (addBtn) {
      addBtn.disabled = !selectedVariant.available;
      addBtn.textContent = selectedVariant.available
        ? `Add to Cart — ${fmt(selectedVariant.price)}`
        : themeString('product', 'soldOut', 'Sold Out');
    }

    if (imgEl) {
      const img = selectedVariant.featured_image || currentProduct.featured_image;
      if (img && img.src) {
        imgEl.src = img.src;
        imgEl.alt = currentProduct.title;
      }
    }
  }

  function bindOptionButtons() {
    const container = $('#qam-options');
    if (!container) return;

    container.querySelectorAll('[data-qam-option]').forEach(btn => {
      on(btn, 'click', () => {
        const key = btn.dataset.qamOption;
        const val = btn.dataset.value;
        selectedOptions[key] = val;

        container.querySelectorAll(`[data-qam-option="${key}"]`).forEach(b => {
          b.classList.toggle('active', b.dataset.value === val);
        });

        const label = container.querySelector(`[data-qam-selected="${key}"]`);
        if (label) label.textContent = val;

        updateVariantUI();
      });
    });
  }

  function renderOptions(product) {
    const container = $('#qam-options');
    if (!container) return;

    const firstAvailable = product.variants.find(v => v.available) || product.variants[0];
    const optionCount = getOptionCount(product);
    selectedOptions = {};

    Array.from({ length: optionCount }, (_, i) => i + 1).forEach(i => {
      selectedOptions[`option${i}`] = firstAvailable[`option${i}`];
    });

    container.innerHTML = Array.from({ length: optionCount }, (_, optIndex) => {
      const optionItem = product.options[optIndex];
      const optionName = getOptionName(optionItem, optIndex);
      const optionKey = `option${optIndex + 1}`;
      const values = getOptionValues(product, optionKey, optIndex);
      const selectedVal = selectedOptions[optionKey];

      if (isColorOption(optionName)) {
        return `
          <div class="qam-option-group">
            <label class="qam-label">${escapeHtml(optionName)}: <span class="qam-selected" data-qam-selected="${optionKey}">${escapeHtml(selectedVal)}</span></label>
            <div class="qam-swatches-row">
              ${values.map(val => `
                <button type="button"
                  class="qam-variant-btn qam-swatch-btn js-color-swatch${val === selectedVal ? ' active' : ''}"
                  data-qam-option="${optionKey}"
                  data-value="${escapeHtml(val)}"
                  data-color="${escapeHtml(val)}"
                  title="${escapeHtml(val)}"
                  aria-label="${escapeHtml(optionName)}: ${escapeHtml(val)}">
                </button>
              `).join('')}
            </div>
          </div>`;
      }

      return `
        <div class="qam-option-group">
          <label class="qam-label">${escapeHtml(optionName)}: <span class="qam-selected" data-qam-selected="${optionKey}">${escapeHtml(selectedVal)}</span></label>
          <div class="qam-btns-row">
            ${values.map(val => `
              <button type="button"
                class="qam-variant-btn${val === selectedVal ? ' active' : ''}"
                data-qam-option="${optionKey}"
                data-value="${escapeHtml(val)}"
                aria-label="${escapeHtml(optionName)}: ${escapeHtml(val)}">
                ${escapeHtml(val)}
              </button>
            `).join('')}
          </div>
        </div>`;
    }).join('');

    bindOptionButtons();
    if (window.ColorSwatch) window.ColorSwatch.applyColors(container);
    updateVariantUI();
  }

  function renderProduct(product) {
    currentProduct = product;

    const vendorEl = $('#qam-vendor');
    const titleEl = $('#qam-title');
    const imgEl = $('#qam-image');
    const viewLink = $('#qam-view-link');
    const qtyField = $('#qam-quantity');

    if (vendorEl) {
      vendorEl.textContent = product.vendor || '';
      vendorEl.style.display = product.vendor ? '' : 'none';
    }
    if (titleEl) titleEl.textContent = product.title;
    if (viewLink) viewLink.href = `/products/${product.handle}`;
    if (qtyField) qtyField.value = '1';

    const image = product.featured_image;
    if (imgEl && image) {
      imgEl.src = image.src;
      imgEl.alt = product.title;
    }

    renderOptions(product);
  }

  async function open(handle) {
    if (!handle || !modal) return;

    show();
    if (loadingEl) loadingEl.hidden = false;
    if (contentEl) contentEl.hidden = true;

    try {
      const res = await window.fetch(`/products/${encodeURIComponent(handle)}.js`);
      if (!res.ok) throw new Error('Product not found');
      const product = await res.json();

      renderProduct(product);
      if (contentEl) contentEl.hidden = false;
    } catch (err) {
      console.error('[QuickAddModal]', err);
      hide();
      Toast.show(themeString('product', 'loadError', 'Could not load product options'), 'error');
    } finally {
      if (loadingEl) loadingEl.hidden = true;
    }
  }

  async function addToCart() {
    if (!selectedVariant || !selectedVariant.available) return;

    const addBtn = $('#qam-add-btn');
    const qtyField = $('#qam-quantity');
    const qty = Math.max(1, parseInt(qtyField?.value || '1', 10));
    const originalText = addBtn?.textContent;

    if (addBtn) {
      addBtn.disabled = true;
      addBtn.innerHTML = `<span class="spinner"></span> ${themeString('cart', 'adding', 'Adding...')}`;
    }

    const ok = await Cart.add(selectedVariant.id, qty);
    if (ok) Toast.show(themeString('cart', 'added', 'Added to cart!'), 'success');

    if (addBtn) {
      addBtn.disabled = !selectedVariant.available;
      addBtn.textContent = originalText || 'Add to Cart';
    }

    if (ok) hide();
  }

  function init() {
    modal = $('#quick-add-modal');
    backdrop = $('#quick-add-modal-backdrop');
    closeBtn = $('#quick-add-modal-close');
    loadingEl = $('#quick-add-modal-loading');
    contentEl = $('#quick-add-modal-content');

    if (!modal) return;

    on(closeBtn, 'click', hide);
    on(backdrop, 'click', hide);
    on($('#qam-add-btn'), 'click', addToCart);

    on(document, 'click', (e) => {
      const btn = e.target.closest('.quick-add-options-btn');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      open(btn.dataset.productHandle);
    });

    on(document, 'keydown', (e) => {
      if (e.key === 'Escape' && isOpen) hide();
    });

    const qtyField = $('#qam-quantity');
    on($('[data-qam-qty="decrease"]'), 'click', () => {
      if (!qtyField) return;
      qtyField.value = Math.max(1, parseInt(qtyField.value || '1', 10) - 1);
    });
    on($('[data-qam-qty="increase"]'), 'click', () => {
      if (!qtyField) return;
      qtyField.value = parseInt(qtyField.value || '1', 10) + 1;
    });
  }

  return { init, open, close: hide };
})();

/* ---------- Color Swatch Map ---------- */
const ColorSwatch = (() => {
  // Full HEX map for Gildan / Gelato / Bella+Canvas colors
  const COLOR_MAP = {
    'white': '#FFFFFF',
    'black': '#1C1C1C',
    'sport grey': '#B5B5B5',
    'sport gray': '#B5B5B5',
    'ash': '#D8D8D3',
    'charcoal': '#4A4A4A',
    'dark heather': '#55565A',
    'heather military green': '#5B6156',
    'military green': '#5A624A',
    'olive': '#61613E',
    'green': '#2E4A36',
    'forest green': '#2E4A36',
    'irish green': '#009A44',
    'lime': '#A6CE39',
    'kelly green': '#007A53',
    'dark green': '#1B4332',
    'army green': '#4B5320',
    'army': '#4B5320',
    'sage': '#9CAF88',
    'sage green': '#9CAF88',
    'hunter green': '#355E3B',
    'hunter': '#355E3B',
    'daisy': '#F7E400',
    'gold': '#D5A021',
    'orange': '#F47C20',
    'safety orange': '#FF6700',
    'texas orange': '#BF5700',
    'cardinal red': '#8C1D40',
    'cardinal': '#8C1D40',
    'red': '#C8102E',
    'maroon': '#6F263D',
    'antique cherry red': '#8A3B45',
    'light pink': '#F5C6D6',
    'azalea': '#F29CB7',
    'heliconia': '#D5006D',
    'purple': '#5F259F',
    'violet': '#7F5BA7',
    'indigo blue': '#294C7A',
    'indigo': '#294C7A',
    'royal': '#0057B8',
    'royal blue': '#0057B8',
    'sapphire': '#0067B8',
    'carolina blue': '#6BA4D9',
    'light blue': '#A9CDEB',
    'navy': '#1F2A44',
    'navy blue': '#1F2A44',
    'midnight': '#2C3443',
    'sky': '#82C7E6',
    'sky blue': '#82C7E6',
    'aqua': '#00B7C7',
    'tropical blue': '#008DB9',
    'sand': '#D8C9A3',
    'natural': '#F2E8D5',
    'cornsilk': '#F5E9B8',
    'tan': '#C7A76C',
    'brown savana': '#4B3621',
    'brown savanna': '#4B3621',
    'chocolate': '#4A2C2A',
    'pink': '#F48CAB',
    'coral': '#FF6B6B',
    'cranberry': '#9B1B30',
    'heather red': '#C0392B',
    'heather navy': '#2C3E6B',
    'heather royal': '#2E6DB4',
    'heather purple': '#6C4B9E',
    'heather gray': '#9EA6AF',
    'heather grey': '#9EA6AF',
    'steel blue': '#4682B4',
    'seafoam': '#71C5A3',
    'mint': '#98D8C8',
    'antique gold': '#C8A008',
    'antique sapphire': '#1B4F8A',
    'galapagos blue': '#0077A8',
  };

  // Colors that need a visible border on white background
  const LIGHT_COLORS = new Set([
    'white', 'natural', 'cornsilk', 'light pink', 'daisy', 'ash', 'light blue', 'sky', 'sky blue', 'sand', 'mint', 'seafoam'
  ]);

  function applyColors(root) {
    (root || document).querySelectorAll('.js-color-swatch').forEach(el => {
      const name = (el.dataset.color || '').trim();
      const key = name.toLowerCase();
      const hex = COLOR_MAP[key] || '#CCCCCC';

      el.style.background = hex;

      if (LIGHT_COLORS.has(key)) {
        el.classList.add('swatch--light');
        el.style.borderColor = '#d1d5db';
      }

      // Tooltip: show color name label on hover
      el.addEventListener('mouseenter', () => showTooltip(el, name));
      el.addEventListener('mouseleave', hideTooltip);
      el.addEventListener('focus', () => showTooltip(el, name));
      el.addEventListener('blur', hideTooltip);
    });
  }

  let tipEl = null;
  function showTooltip(el, name) {
    hideTooltip();
    if (!name) return;
    tipEl = document.createElement('div');
    tipEl.className = 'swatch-tooltip';
    tipEl.textContent = name;
    document.body.appendChild(tipEl);
    const r = el.getBoundingClientRect();
    tipEl.style.left = (r.left + r.width / 2 - tipEl.offsetWidth / 2 + window.scrollX) + 'px';
    tipEl.style.top = (r.top - tipEl.offsetHeight - 6 + window.scrollY) + 'px';
  }
  function hideTooltip() {
    if (tipEl) { tipEl.remove(); tipEl = null; }
  }

  function init() {
    applyColors();
  }

  return { init, applyColors };
})();


/* ---------- Predictive Search (AJAX) ---------- */
const SearchModal = (() => {
  let debounceTimer = null;
  let activeIndex = -1;
  let abortController = null;
  let requestId = 0;
  let currentQuery = '';

  const modal = () => $('#search-modal');
  const input = () => $('#search-modal-input');
  const clearBtn = () => $('#search-modal-clear');
  const loading = () => $('#search-loading');
  const noResult = () => $('#search-no-results-state');
  const panel = () => $('#search-results-panel');
  const list = () => $('#search-results-list');
  const defState = () => $('#search-default-state');
  const seeAll = () => $('#search-see-all-link');
  const viewAll = () => $('#search-view-all-link');
  const noTerm = () => $('#no-results-term');
  const openBtn = () => $('#search-open-btn');
  const form = () => $('#search-modal-form');

  function show(el) { el && (el.hidden = false); }
  function hide(el) { el && (el.hidden = true); }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function setView(state) {
    hide(defState());
    hide(loading());
    hide(noResult());
    hide(panel());

    if (state === 'default') show(defState());
    else if (state === 'loading') show(loading());
    else if (state === 'empty') show(noResult());
    else if (state === 'results') show(panel());
  }

  function open() {
    const m = modal();
    if (!m) return;
    m.hidden = false;
    document.body.style.overflow = 'hidden';
    openBtn()?.setAttribute('aria-expanded', 'true');
    resetResults();
    setTimeout(() => {
      input()?.focus();
      FocusTrap.activate(m, { initialFocus: input() });
    }, 80);
  }

  function close() {
    const m = modal();
    if (!m) return;
    m.hidden = true;
    document.body.style.overflow = '';
    openBtn()?.setAttribute('aria-expanded', 'false');
    FocusTrap.deactivate(m);

    if (abortController) abortController.abort();
    abortController = null;
    clearTimeout(debounceTimer);
    currentQuery = '';

    resetResults();
    const inp = input();
    if (inp) inp.value = '';
    hide(clearBtn());
  }

  function resetResults() {
    setView('default');
    activeIndex = -1;
    if (list()) list().innerHTML = '';
  }

  function highlightQuery(text, query) {
    const safeText = escapeHtml(text);
    const safe = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return safeText.replace(new RegExp(`(${safe})`, 'gi'), '<mark>$1</mark>');
  }

  function moneyFmt(price) {
    const num = parseFloat(price);
    if (isNaN(num)) return '';
    return fmt(Math.round(num * 100));
  }

  async function search(query) {
    const q = (query || '').trim();
    currentQuery = q;

    if (q.length < 2) {
      if (abortController) abortController.abort();
      abortController = null;
      resetResults();
      return;
    }

    if (abortController) abortController.abort();
    abortController = new AbortController();
    const thisId = ++requestId;

    setView('loading');

    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=8&resources[fields]=title,vendor,product_type,variants.title`;
      const res = await window.fetch(url, { signal: abortController.signal });
      if (!res.ok) throw new Error('Search request failed');
      const data = await res.json();

      if (thisId !== requestId || currentQuery !== q) return;

      const products = data?.resources?.results?.products || [];

      if (products.length === 0) {
        if (noTerm()) noTerm().textContent = q;
        if (viewAll()) viewAll().href = `/search?type=product&q=${encodeURIComponent(q)}`;
        setView('empty');
        return;
      }

      const searchUrl = `/search?type=product&q=${encodeURIComponent(q)}`;
      if (seeAll()) seeAll().href = searchUrl;

      list().innerHTML = products.map((p, i) => {
        const imgSrc = p.image || (p.featured_image && p.featured_image.url) || '';
        const safeTitle = escapeHtml(p.title);
        const img = imgSrc
          ? `<img class="search-result-img" src="${escapeHtml(imgSrc)}" alt="${safeTitle}" width="56" height="56" loading="lazy">`
          : `<div class="search-result-img-placeholder">🛍️</div>`;
        const price = p.price ? moneyFmt(p.price) : (p.price_min ? moneyFmt(p.price_min) : '');
        const title = highlightQuery(p.title, q);
        const safeUrl = escapeHtml(p.url || '#');
        const vendor = p.vendor ? `<div class="search-result-vendor">${escapeHtml(p.vendor)}</div>` : '';
        return `
          <li role="option" aria-selected="false" id="search-result-${i}">
            <a href="${safeUrl}" class="search-result-item" tabindex="-1">
              ${img}
              <div class="search-result-info">
                <div class="search-result-title">${title}</div>
                ${vendor}
              </div>
              ${price ? `<div class="search-result-price">${price}</div>` : ''}
            </a>
          </li>`;
      }).join('');

      setView('results');
      activeIndex = -1;
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[Search] Error:', err);
      if (thisId === requestId) {
        setView('default');
        if (window.Toast) window.Toast.show('Search is temporarily unavailable', 'error');
      }
    }
  }

  function handleKeyboard(e) {
    const m = modal();
    if (!m || m.hidden) return;

    if (e.key === 'Escape') {
      close();
      return;
    }

    const items = list()?.querySelectorAll('.search-result-item') || [];
    if (!items.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      activeIndex = Math.min(activeIndex + 1, items.length - 1);
      updateActive(items);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      activeIndex = Math.max(activeIndex - 1, -1);
      updateActive(items);
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      items[activeIndex]?.click();
    }
  }

  function updateActive(items) {
    items.forEach((item, i) => {
      item.closest('li')?.setAttribute('aria-selected', i === activeIndex ? 'true' : 'false');
      if (i === activeIndex) item.closest('li').scrollIntoView({ block: 'nearest' });
    });
  }

  function init() {
    const op = openBtn();
    const inp = input();
    const cl = clearBtn();
    const bd = $('#search-modal-backdrop');
    const closeButton = $('#search-modal-close');
    const searchForm = form();

    if (!op || !inp) return;

    on(op, 'click', open);
    on(closeButton, 'click', close);
    on(bd, 'click', close);
    on(document, 'keydown', handleKeyboard);

    on(searchForm, 'submit', (e) => {
      e.preventDefault();
      const q = inp.value.trim();
      if (q.length < 2) return;
      window.location.href = `/search?type=product&q=${encodeURIComponent(q)}`;
    });

    on(inp, 'input', () => {
      const q = inp.value.trim();
      if (cl) cl.hidden = !q;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => search(q), 300);
    });

    on(cl, 'click', () => {
      inp.value = '';
      cl.hidden = true;
      inp.focus();
      if (abortController) abortController.abort();
      abortController = null;
      currentQuery = '';
      resetResults();
    });

    $$('.trending-tag').forEach(tag => {
      on(tag, 'click', () => {
        const q = tag.dataset.query || '';
        if (!q) return;
        inp.value = q;
        if (cl) cl.hidden = false;
        search(q);
      });
    });
  }

  return { init, open, close };
})();

/* ---------- Free Shipping Bar ---------- */
const FreeShippingBar = (() => {
  function update(cartTotalCents) {
    const total = Math.max(0, parseInt(cartTotalCents, 10) || 0);

    $$('[data-free-shipping-bar]').forEach(bar => {
      const threshold = parseInt(bar.dataset.threshold, 10) || 5000;
      const msgEl = bar.querySelector('[data-free-shipping-msg]');
      const fillEl = bar.querySelector('[data-free-shipping-fill]');
      if (!msgEl || !fillEl) return;

      const progress = Math.min(100, Math.round((total / threshold) * 100));
      const remaining = Math.max(0, threshold - total);

      if (total >= threshold) {
        msgEl.innerHTML = 'You\'ve unlocked <strong>free shipping!</strong>';
        bar.classList.add('is-complete');
        fillEl.style.width = '100%';
      } else if (total === 0) {
        msgEl.innerHTML = `Spend <strong>${fmt(threshold)}</strong> to Free shipping`;
        bar.classList.remove('is-complete');
        fillEl.style.width = '0%';
      } else {
        msgEl.innerHTML = `Add <strong>${fmt(remaining)}</strong> more for <strong>free shipping!</strong>`;
        bar.classList.remove('is-complete');
        fillEl.style.width = `${progress}%`;
      }

      bar.dataset.cartTotal = total;
    });
  }

  function init() {
    $$('[data-free-shipping-bar]').forEach(bar => {
      update(parseInt(bar.dataset.cartTotal, 10) || 0);
    });
  }

  return { init, update };
})();

/* ---------- Cart Discount Code ---------- */
const CartDiscount = (() => {
  function applyCode(code) {
    const clean = String(code || '').trim();
    if (!clean) return false;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    // Shopify applies the code to the session, then returns to cart
    window.location.href = `${root}discount/${encodeURIComponent(clean)}?redirect=/cart`;
    return true;
  }

  function init() {
    const btn = $('#apply-coupon-btn') || $('[data-apply-discount]');
    const input = $('#cart-coupon');
    if (!btn || !input) return;

    const run = () => {
      const code = input.value.trim();
      const feedback = $('[data-discount-feedback]');
      if (!code) {
        if (feedback) {
          feedback.hidden = false;
          feedback.style.color = '#b91c1c';
          feedback.textContent = themeString('cart', 'discountEmpty', 'Enter a discount code');
        } else {
          Toast.show(themeString('cart', 'discountEmpty', 'Enter a discount code'), 'error');
        }
        input.focus();
        return;
      }
      btn.disabled = true;
      btn.textContent = themeString('cart', 'discountApplying', 'Applying…');
      applyCode(code);
    };

    on(btn, 'click', run);
    on(input, 'keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        run();
      }
    });
  }

  return { init };
})();

/* ---------- Promo Code Copy ---------- */
const PromoCopy = (() => {
  function init() {
    on(document, 'click', async (e) => {
      const btn = e.target.closest('[data-copy-promo]');
      if (!btn) return;
      const code = btn.dataset.copyPromo;
      if (!code) return;
      try {
        await navigator.clipboard.writeText(code);
        Toast.show(themeString('promo', 'copySuccess', 'Code copied!'), 'success');
      } catch {
        Toast.show(themeString('promo', 'copyError', 'Could not copy code'), 'error');
      }
    });
  }
  return { init };
})();

/* ---------- Init ---------- */
document.addEventListener('DOMContentLoaded', () => {
  Toast.init();
  Header.init();
  MobileNav.init();
  Cart.init();
  ProductForm.init();
  VariantSelector.init();
  QtyInput.init();
  VolumeBuy.init();
  AnnouncementBar.init();
  ScrollAnim.init();
  Gallery.init();
  StickyATC.init();
  LazyImage.init();
  QuickAdd.init();
  QuickAddModal.init();
  ColorSwatch.init();
  SearchModal.init();
  FreeShippingBar.init();
  PromoCopy.init();
  CartDiscount.init();

  // Expose modules needed by section inline scripts
  window.LazyImage = LazyImage;
  window.ScrollAnim = ScrollAnim;
  window.Toast = Toast;
  window.ColorSwatch = ColorSwatch;
  window.QuickAddModal = QuickAddModal;
  window.FreeShippingBar = FreeShippingBar;
});
